/**
 * Conversation Storage Utility
 * Manages conversation caching using sessionStorage
 * Fetches from API on first load or when cache expires
 */

import { Conversation } from '../types';
import { messagingServiceApi } from './messagingServiceApi';

const STORAGE_KEY = 'hush_conversations_cache';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes default

interface ConversationsCache {
  data: Conversation[];
  timestamp: number;
  has_more: boolean;
}

/**
 * Conversation Storage Manager
 * Handles fetching, caching, and retrieving conversations
 */
class ConversationStorage {
  private cacheDuration: number;

  constructor(cacheDurationMs: number = CACHE_DURATION_MS) {
    this.cacheDuration = cacheDurationMs;
  }

  /**
   * Check if cached data exists and is still valid
   */
  private isCacheValid(): boolean {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (!cached) {
        return false;
      }

      const cache: ConversationsCache = JSON.parse(cached);
      const now = Date.now();
      const age = now - cache.timestamp;

      return age < this.cacheDuration;
    } catch (error) {
      console.error('Error checking cache validity:', error);
      return false;
    }
  }

  /**
   * Get cached conversations from sessionStorage
   */
  private getCachedConversations(): ConversationsCache | null {
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (!cached) {
        return null;
      }

      return JSON.parse(cached);
    } catch (error) {
      console.error('Error reading cached conversations:', error);
      return null;
    }
  }

  /**
   * Save conversations to sessionStorage with timestamp
   */
  private saveToCache(conversations: Conversation[], has_more: boolean): void {
    try {
      const cache: ConversationsCache = {
        data: conversations,
        timestamp: Date.now(),
        has_more,
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('Error saving conversations to cache:', error);
    }
  }

  /**
   * Clear cached conversations
   */
  clearCache(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Get conversations - from cache if valid, otherwise fetch from API
   *
   * @param forceRefresh If true, bypasses cache and fetches from API
   * @param limit Number of conversations to fetch (default 20)
   * @param skip Offset for pagination (default 0)
   * @returns Promise with conversations array and has_more flag
   */
  async getConversations(
    forceRefresh: boolean = false,
    limit: number = 20,
    skip: number = 0
  ): Promise<{ conversations: Conversation[]; has_more: boolean }> {
    // Check cache first (unless force refresh)
    if (!forceRefresh && this.isCacheValid()) {
      const cached = this.getCachedConversations();
      if (cached) {
        console.log('📦 Using cached conversations (age: ' +
          Math.round((Date.now() - cached.timestamp) / 1000) + 's)');
        return {
          conversations: cached.data,
          has_more: cached.has_more,
        };
      }
    }

    // Fetch from API
    console.log('🔄 Fetching conversations from API...');
    try {
      const response = await messagingServiceApi.getConversations({ limit, skip });

      // Ensure conversations is always an array
      const conversations = response?.conversations || [];
      const hasMore = response?.has_more || false;

      // Save to cache
      this.saveToCache(conversations, hasMore);

      console.log('✅ Conversations fetched and cached:', conversations.length);

      return {
        conversations,
        has_more: hasMore,
      };
    } catch (error) {
      console.error('❌ Failed to fetch conversations:', error);

      // Fallback to cached data even if expired
      const cached = this.getCachedConversations();
      if (cached) {
        console.log('⚠️ Using stale cached data as fallback');
        return {
          conversations: cached.data,
          has_more: cached.has_more,
        };
      }

      // Return empty array instead of throwing to prevent app crash
      console.log('⚠️ No cached data available, returning empty array');
      return {
        conversations: [],
        has_more: false,
      };
    }
  }

  /**
   * Update a single conversation in the cache
   * Useful when a new message arrives via WebSocket
   *
   * @param updatedConversation The conversation to update
   */
  updateConversationInCache(updatedConversation: Conversation): void {
    try {
      const cached = this.getCachedConversations();
      if (!cached) {
        return;
      }

      const index = cached.data.findIndex(
        (c) => c.application_id === updatedConversation.application_id
      );

      if (index !== -1) {
        // Update existing conversation
        cached.data[index] = updatedConversation;
      } else {
        // Add new conversation to the beginning
        cached.data.unshift(updatedConversation);
      }

      // Sort by last_message_at (most recent first)
      cached.data.sort((a, b) => {
        const timeA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const timeB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return timeB - timeA;
      });

      // Save updated cache
      this.saveToCache(cached.data, cached.has_more);

      console.log('✅ Conversation updated in cache:', updatedConversation.application_id);
    } catch (error) {
      console.error('Error updating conversation in cache:', error);
    }
  }

  /**
   * Mark a conversation as read in the cache
   *
   * @param applicationId The application ID of the conversation
   * @param userRole The role of the current user
   */
  markAsReadInCache(applicationId: string, userRole: 'candidate' | 'recruiter'): void {
    try {
      const cached = this.getCachedConversations();
      if (!cached) {
        return;
      }

      const conversation = cached.data.find(
        (c) => c.application_id === applicationId
      );

      if (conversation) {
        if (userRole === 'candidate') {
          conversation.unread_count.candidate = 0;
        } else {
          conversation.unread_count.recruiter = 0;
        }

        this.saveToCache(cached.data, cached.has_more);
        console.log('✅ Conversation marked as read in cache:', applicationId);
      }
    } catch (error) {
      console.error('Error marking conversation as read in cache:', error);
    }
  }

  /**
   * Get cache age in milliseconds
   * Returns null if no cache exists
   */
  getCacheAge(): number | null {
    try {
      const cached = this.getCachedConversations();
      if (!cached) {
        return null;
      }

      return Date.now() - cached.timestamp;
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
export const conversationStorage = new ConversationStorage();

// Export class for custom instances
export { ConversationStorage };
